import { inject, Service } from '@angular/core';
import { SendPsCustomersRequestDtoInput, UpsertCustomerDtoInput } from '@h5s/dtos-api';
import {
  Customer,
  GetAllOwnerPublicSecurityGql,
  GetAllOwnerPublicSecurityQueryResult,
  GetPsCustomerGql,
  GetPsCustomerQueryResult,
  GetPsCustomerQueryVariables,
  OwnerPublicSecurity,
  PsCustomer,
  ResourceValidationResult,
  SendPsCustomer,
  SendPsCustomersGql,
  SendPsCustomersMutationResult,
  SendPsCustomersMutationVariables,
  UpdateCustomer,
  ValidateCustomerForPsGql,
  ValidateCustomerForPsQueryResult,
  ValidateCustomerForPsQueryVariables,
} from '@h5s/entities';
import { throwErrorIfNullFromGraphQL } from '@h5s/util-error';
import { mapArrayResponse, mapParamDto } from '@h5s/util-object';
import { transformDateTimeToLocalDate } from '@h5s/util-validation';
import { Apollo } from 'apollo-angular';
import { DateTime } from 'luxon';
import { map, Observable } from 'rxjs';

@Service()
export class DataAccessPsService {
  private readonly apollo = inject(Apollo);

  public getOwnerPublicSecurity(): Observable<OwnerPublicSecurity[]> {
    return this.apollo
      .query<GetAllOwnerPublicSecurityQueryResult>({
        query: GetAllOwnerPublicSecurityGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(OwnerPublicSecurity, result.getAllOwnerPublicSecurity)),
      );
  }

  public getPsCustomer(date: DateTime, ownerPublicSecurityId: number): Observable<PsCustomer[]> {
    const variables: GetPsCustomerQueryVariables = {
      ownerPublicSecurityId: ownerPublicSecurityId,
      date: transformDateTimeToLocalDate(date),
    };

    return this.apollo
      .query<GetPsCustomerQueryResult>({
        query: GetPsCustomerGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(PsCustomer, result.getPsCustomer)),
      );
  }

  /**
   * Send PsCustomers
   *
   * @param {DateTime} date
   * @param {number} ownerPublicSecurityId
   * @param {number[]} purchasedStayToCustomerIdsToInclude
   * @returns {Observable<boolean>}
   */
  public sendPsCustomers(
    date: DateTime,
    ownerPublicSecurityId: number,
    purchasedStayToCustomerIdsToInclude: number[],
  ): Observable<boolean> {
    const sendPsCustomer = new SendPsCustomer({
      ownerPublicSecurityId: ownerPublicSecurityId,
      date: date,
      purchasedStayToCustomerIdsToInclude: purchasedStayToCustomerIdsToInclude,
    });

    const sendPsCustomersRequestDTO = mapParamDto<SendPsCustomersRequestDtoInput>(sendPsCustomer);

    const variables: SendPsCustomersMutationVariables = {
      sendPsCustomersRequestDTO,
    };

    return this.apollo
      .mutate<SendPsCustomersMutationResult>({
        mutation: SendPsCustomersGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => result.sendPsCustomers),
      );
  }

  /**
   * Validate customer for PS
   *
   * @param {UpdateCustomer} customer
   * @returns {Observable<ResourceValidationResult<Customer>[]>}
   */
  public validateCustomerForPs(customer: UpdateCustomer): Observable<ResourceValidationResult<Customer>[]> {
    const upsertCustomerDTO = mapParamDto<UpsertCustomerDtoInput>(customer);

    const variables: ValidateCustomerForPsQueryVariables = {
      upsertCustomerDTO,
    };

    return this.apollo
      .query<ValidateCustomerForPsQueryResult>({
        query: ValidateCustomerForPsGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(ResourceValidationResult<Customer>, result.validateCustomerForPs)),
      );
  }
}
